/**
 * AdminAuditScores — Super-Admin overview: Compliance/Datenschutz-Score per Mandant.
 *
 * Visual: traffic-light cards (rot/amber/grün) sorted by risk (highest risk first).
 * Each card shows score, breakdown table and download-button for audit-trail-PDF.
 */
import { useEffect, useState } from "react";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Download, Loader2,
  Building2, BarChart3,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const RISK_STYLES = {
  high:   { ring: "border-red-500/50",    bg: "bg-red-950/30",   pill: "bg-red-500 text-white",   label: "Hohes Risiko" },
  medium: { ring: "border-amber-500/50",  bg: "bg-amber-950/30", pill: "bg-amber-400 text-black", label: "Mittleres Risiko" },
  low:    { ring: "border-emerald-500/50",bg: "bg-emerald-950/30", pill: "bg-emerald-500 text-black", label: "Geringes Risiko" },
};

export default function AdminAuditScores() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/superadmin/compliance/audit-scores")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, []);

  const downloadAuditTrail = async (tenantId, tenantSlug) => {
    try {
      const r = await api.get(`/superadmin/compliance/${tenantId}/audit-trail-pdf`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-trail-${tenantSlug}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-zinc-500" /></div>;
  if (!data) return null;

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="admin-audit-scores-page">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="text-[#00E575]" size={22} />
        <h1 className="font-display text-2xl font-bold">Datenschutz-Audit-Score</h1>
      </div>
      <p className="text-zinc-300 text-sm font-medium">
        Pro Mandant berechnet aus AVV/DSGVO/TOMs-Signatur (70 P), DNC-Aktivität (10 P)
        und Lead-Routing-Consent-Anteil (20 P). Mandanten sortiert nach Score, niedrigste zuerst.
      </p>

      {/* Summary banner */}
      <div className="grid grid-cols-3 gap-3" data-testid="audit-score-summary">
        <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-4">
          <div className="text-[12px] uppercase tracking-wider text-zinc-300 font-bold">Mandanten gesamt</div>
          <div className="text-2xl font-bold text-zinc-100 mt-1">{data.total}</div>
        </div>
        <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-4">
          <div className="text-[12px] uppercase tracking-wider text-zinc-300 font-bold">Durchschnitts-Score</div>
          <div className={`text-2xl font-bold mt-1 ${data.avg_score >= 80 ? "text-emerald-400" : data.avg_score >= 50 ? "text-amber-400" : "text-red-400"}`}>
            {data.avg_score} %
          </div>
        </div>
        <div className="bg-[#18181B] border border-zinc-800 rounded-xl p-4">
          <div className="text-[12px] uppercase tracking-wider text-zinc-300 font-bold">Hochrisiko-Mandanten</div>
          <div className="text-2xl font-bold text-red-400 mt-1">
            {data.tenants.filter(t => t.risk === "high").length}
          </div>
        </div>
      </div>

      {/* Tenant cards */}
      <div className="space-y-3">
        {data.tenants.map((t) => (
          <TenantCard key={t.tenant_id} t={t} onDownload={downloadAuditTrail} />
        ))}
      </div>

      {/* Legend */}
      <details className="bg-[#18181B] border border-zinc-800 rounded-xl p-4 mt-4" data-testid="audit-score-legend">
        <summary className="cursor-pointer text-[12px] font-bold text-zinc-200 uppercase tracking-wider">
          Score-Berechnung im Detail
        </summary>
        <ul className="text-[12px] text-zinc-400 mt-3 space-y-1.5 font-medium">
          <li>• <b className="text-zinc-200">AVV signiert</b> → 30 Punkte</li>
          <li>• <b className="text-zinc-200">DSGVO-Verpflichtung signiert</b> → 20 Punkte</li>
          <li>• <b className="text-zinc-200">TOMs signiert</b> → 20 Punkte</li>
          <li>• <b className="text-zinc-200">DNC-Liste aktiv gepflegt</b> (≥1 Eintrag) → 10 Punkte</li>
          <li>• <b className="text-zinc-200">Routing-Consent-Anteil</b> → 0–20 Punkte (anteilig)</li>
          <li className="text-amber-400 mt-2">Bestandsmandanten („legacy") erhalten die 70 Doku-Punkte als Grandfathering.</li>
        </ul>
      </details>
    </div>
  );
}

const TenantCard = ({ t, onDownload }) => {
  const style = RISK_STYLES[t.risk] || RISK_STYLES.medium;
  return (
    <div className={`border rounded-xl p-4 ${style.ring} ${style.bg}`}
         data-testid={`audit-score-card-${t.tenant_slug}`}>
      <div className="flex items-start gap-3">
        <Building2 className="text-zinc-400 flex-shrink-0 mt-1" size={20} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-zinc-100">{t.tenant_name}</h3>
            <span className="text-[12px] text-zinc-300">· {t.partner_type}</span>
            {t.is_legacy_tenant && (
              <span className="text-[9px] uppercase tracking-wider bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">legacy</span>
            )}
            {!t.active && (
              <span className="text-[9px] uppercase tracking-wider bg-red-900 text-red-200 px-1.5 py-0.5 rounded">inaktiv</span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px] mt-2">
            <Stat label="AVV" ok={t.breakdown.avv_signed} />
            <Stat label="DSGVO" ok={t.breakdown.dsgvo_signed} />
            <Stat label="TOMs" ok={t.breakdown.toms_signed} />
            <Stat label={`DNC (${t.breakdown.dnc_entries})`} ok={t.breakdown.dnc_entries > 0} />
          </div>
          <div className="text-[12px] text-zinc-300 mt-2">
            Routing-Consent: {t.breakdown.routing_consent_pct ?? "—"}%
            {" "}({t.breakdown.leads_with_routing_consent} / {t.breakdown.leads_total} Telesales-Leads)
          </div>
        </div>
        <div className="text-right flex-shrink-0 flex flex-col items-end gap-2">
          <div className={`px-3 py-1 rounded-full text-[12px] font-bold ${style.pill}`}>
            {t.score} / 100
          </div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-300">{style.label}</div>
          <button onClick={() => onDownload(t.tenant_id, t.tenant_slug)}
                  data-testid={`audit-download-${t.tenant_slug}`}
                  className="px-2.5 h-7 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[12px] inline-flex items-center gap-1">
            <Download size={10} /> PDF
          </button>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, ok }) => (
  <div className="flex items-center gap-1.5">
    {ok === true
      ? <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={12} />
      : ok === "legacy"
        ? <ShieldCheck className="text-zinc-500 flex-shrink-0" size={12} />
        : <AlertTriangle className="text-amber-400 flex-shrink-0" size={12} />}
    <span className={ok === true ? "text-emerald-300" : ok === "legacy" ? "text-zinc-400" : "text-amber-300"}>
      {label}
    </span>
  </div>
);
